// @ts-nocheck: converted from JS

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import StepForm from '@/components/UI/StepForm';
import SubHeader from '@/components/UI/SubHeader';
import { successToast } from '@/helpers';
import { _isEmpty, _isNotEmpty, _notNil } from '@/littledash';
import { useFetchEntity } from '@/support/Hooks/fetch';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { createMeta } from '@/support/hot';
import Http from '@/support/http';
import { api as apiRoute, web as webRoute } from '@/support/route';
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { parseQueryString } from '@/helpers';
import { featuresSelector } from '@/support/Selectors';
import { AnimalSexLabelToCode } from '@/model/Animal.utils';
import type { AnimalSexLabel } from '@/model/Animal.model';
import { initAnimalSettings } from '../Colony.utils';
import { initCageSettings } from './Cages.utils';
import Animals from './Animals';
import Summary from './Summary';
import Cages from './Cages';
import './../Colony.scss';
import { useEntitySchemaFieldsByType } from '@/utils/entitySchema/useEntitySchema';

const featureFlagSelector = createSelector([featuresSelector], (features) => ({
  unknownSexEnabled: features?.unknown_sex === true,
}));

const ColonyCreation = () => {
  const location = useLocation();
  const { study: studyId } = parseQueryString(location.search);
  const navigate = useNavigate();
  const { unknownSexEnabled } = useSelector(featureFlagSelector);

  const ColonyCreationCages = _notNil(studyId) ? withStudy(Cages, studyId) : Cages;
  const entityFieldRequiredMap = useEntitySchemaFieldsByType('animal');

  const [studyApiId, setStudyApiId] = useState<string | null>(null);

  useEffect(() => {
    if (_notNil(studyId)) {
      Http.get(apiRoute('studies.show.p', { id: studyId }))
        .then((response) => {
          setStudyApiId(response?.data?.data?.api_id ?? null);
        })
        .catch(() => {
          setStudyApiId(null);
        });
    }
  }, [studyId]);

  const { invoke: createCages } = useApiHook({
    endpoint: 'POST /api/v1/cages',
    body: { cages: [] },
    options: {
      invokeOnInit: false,
      onError: { slug: 'colony-create-cages', capture: true, toast: false },
    },
  });

  const returnText = _notNil(studyId) ? 'Cages' : 'Colony';
  const returnLink = _notNil(studyId) ? webRoute('studies.cages', { id: studyId }) : webRoute('colony');

  const steps = [
    {
      name: 'Cages',
      isValid: ({ cages: { cellErrors } }) => {
        const cellErrorValues = Object.values(cellErrors);
        if (_isNotEmpty(cellErrorValues) || loading) {
          return cellErrorValues[cellErrorValues.length - 1];
        }
        return true;
      },
      renderStep: (state, dispatch) => <ColonyCreationCages state={state} dispatch={dispatch} />,
    },
    {
      name: 'Add animals',
      isValid: ({ animals: { cellErrors } }) => {
        if (_isNotEmpty(Object.values(cellErrors))) {
          return Object.values(cellErrors).join('</br>');
        }
        return true;
      },
      renderStep: (state, dispatch) => (
        <Animals state={state} dispatch={dispatch} entityFieldRequiredMap={entityFieldRequiredMap} />
      ),
    },
    {
      name: 'Summary',
      isValid: (state) => true,
      renderStep: (state, dispatch) => <Summary state={state} dispatch={dispatch} />,
    },
  ];
  const initialState = {
    steps,
    currentStep: 0,
    complete: false,
    stepReady: false,
    cages: {
      initialData: [],
      data: [],
      settings: initCageSettings(),
      metadata: [],
      metaGlossaries: [],
      cellErrors: { 'no-cage': 'You must supply at least 1 cage' },
      overlay: '',
      invalidCells: {},
      cageCount: 1,
      maxRows: 0,
    },
    animals: {
      species: undefined,
      initialData: [],
      data: [],
      settings: initAnimalSettings(
        [],
        true,
        false,
        undefined,
        undefined,
        undefined,
        entityFieldRequiredMap,
        unknownSexEnabled
      ).settings,
      cellErrors: {},
      metadata: [],
      metaGlossaries: [],
      invalidCells: {},
      overlay: '',
    },
    Summary: {},
    apiError: undefined,
  };
  const [loading, setLoading] = useState(false);

  const formatCurrentCageAnimals = (cageCatalog, state) => {
    const {
      animals: { settings: animalSettings, data: animalData, species, metaGlossaries },
    } = state;
    const animalColumns = animalSettings?.columns;
    const metaFormatter = createMeta;
    const glossaryIdToApiId = (metaGlossaries ?? []).reduce((acc, g) => {
      if (_notNil(g.id) && _notNil(g.api_id)) {
        acc[String(g.id)] = g.api_id;
      }
      return acc;
    }, {});
    return animalData.reduce((acc, { cage_catalog: catalog, name, sex, species: animalSpecies, dob, strain, meta }) => {
      if (cageCatalog === catalog) {
        const returnData = {
          name,
        };
        if (sex) {
          returnData.sex = AnimalSexLabelToCode[sex as AnimalSexLabel];
        }
        if (dob) {
          returnData.dob = dob;
        }
        if (animalSpecies) {
          const strains = species.find((species) => species.name === animalSpecies).strains;
          if (strains.length) {
            const matchingStrain = strains.find((s) => s.name === strain);
            if (matchingStrain?.api_id) {
              returnData.strain_id = matchingStrain.api_id;
            }
          }
        }
        if (meta && _isNotEmpty(meta)) {
          returnData.metadata = metaFormatter(meta, animalColumns)
            .filter((m) => _notNil(m.glossary_id))
            .map((m) => ({
              glossary_id: glossaryIdToApiId[String(m.glossary_id)] ?? String(m.glossary_id),
              value: m.value,
            }));
        }
        acc.push(returnData);
      }
      return acc;
    }, []);
  };

  const updateApiError = (dispatch, apiError) => {
    dispatch({
      type: 'update',
      id: 'animals',
      data: apiError,
    });
  };

  const formatCageData = (state) => {
    const {
      cages: { data: cageData, settings, metaGlossaries },
    } = state;
    const columns = settings?.columns;
    const cageMetaFormatter = createMeta;
    const glossaryIdToApiId = (metaGlossaries ?? []).reduce((acc, g) => {
      if (_notNil(g.id) && _notNil(g.api_id)) {
        acc[String(g.id)] = g.api_id;
      }
      return acc;
    }, {});
    return cageData
      .filter((cage) => cage.name)
      .map(({ catalog, name, population, meta }) => ({
        name,
        study_id: studyApiId,
        metadata: _isEmpty(meta)
          ? []
          : cageMetaFormatter(meta, columns)
              .filter((m) => _notNil(m.glossary_id))
              .map((m) => ({
                glossary_id: glossaryIdToApiId[String(m.glossary_id)] ?? String(m.glossary_id),
                value: m.value,
              })),
        animals: formatCurrentCageAnimals(catalog, state),
      }));
  };

  const saveColony = async (state, dispatch) => {
    if (_notNil(studyId) && !studyApiId) {
      updateApiError(dispatch, 'Unable to resolve study. Please try again.');
      return;
    }
    setLoading(true);
    const formattedData = formatCageData(state);
    try {
      const response = await createCages({ body: { cages: formattedData } });
      const data = response?.body ?? [];
      navigate('/colony');
      successToast(`Successfully added ${data.length > 1 ? 'colonies' : 'colony'}`);
      if (studyId) {
        navigate(
          webRoute('studies.cages', {
            id: studyId,
          })
        );
      }
    } catch {
      updateApiError(dispatch, 'Failed to create Colony or Animals');
    } finally {
      setLoading(false);
    }
  };
  const errorBanner = ({ state: { apiError } }) => (
    <>
      {apiError && (
        <ApiErrorBanner
          className="mb4"
          title={apiError}
          text={
            'An error has occurred when submitting your request, please try again later. If this keeps occurring please contact support.'
          }
          error={apiError}
        />
      )}
    </>
  );
  return (
    <div className="h-100">
      <SubHeader linkToText={returnText} link={returnLink} />

      <div className="pa4">
        {loading ? (
          <div style={{ height: 650 }}>
            <Loading txt="Updating colony..." />
          </div>
        ) : (
          <StepForm
            title="Add new cages and animals"
            titlePadding="pb2"
            initialState={initialState}
            props={{}}
            steps={steps}
            finalStepAction={saveColony}
            finalButtonText="Create"
            finalButtonClass="w-47 mw6 center"
            horizontal
            errorBanner={errorBanner}
          />
        )}
      </div>
    </div>
  );
};

const withStudy = (CageComponent, studyId) => (props) => {
  const { entity: study } = useFetchEntity({
    entityType: 'study',
    params: { id: studyId },
    includes: 'counts',
  });

  return <CageComponent study={study} {...props} />;
};

export default ColonyCreation;
